import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Pencil, X, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const EMPTY = { title_en: "", title_fr: "", excerpt_en: "", excerpt_fr: "", content_en: "", content_fr: "", category: "Story", cover: "", author: "Gesung Team", read_time: 4, published: true };

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {..post} | 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); };
  const close = () => setEditing(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, read_time: Number(form.read_time) || 4 };
      if (editing === "new") {
        await api.post("/admin/blog", payload);
        toast.success("Story published");
      } else {
        await api.patch(`/admin/blog/${editing.id}`, payload);
        toast.success("Story updated");
      }
      close();
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await api.patch(`/admin/blog/${p.id}`, { published: !p.published });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Story deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-white/40">Journal · {posts.length} stories</p>
        <button onClick={openNew} data-testid="blog-new-btn" className="inline-flex items-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-lg px-4 h-10 text-sm transition-colors">
          <Plus size={16} /> New story
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#181818] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="blog-table">
            <thead>
              <tr className="text-left text-white/40 text-xs uppercase tracking-[0.15em] border-b border-white/10">
                <th className="px-5 py-4 font-medium">Title</th>
                <th className="px-5 py-4 font-medium">Category</th>
                <th className="px-5 py-4 font-medium">Status</th>
                <th className="px-5 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-white/40">No stories yet.</td></tr>
              ) : posts.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`blog-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-medium">{p.title_en}</p>
                    <p className="text-white/40 text-xs">{p.author} · {p.read_time} min · FR {p.title_fr ? "✓" : "—"}</p>
                  </td>
                  <td className="px-5 py-4 text-white/60">{p.category}</td>
                  <td className="px-5 py-4">
                    <button onClick={() => togglePublish(p)} data-testid={`blog-publish-${p.id}`} className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border ${p.published ? "text-lime2 border-lime2/40 bg-lime2/10" : "text-white/50 border-white/20"}`}>
                      {p.published ? <Eye size={13} /> : <EyeOff size={13} />} {p.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-3">
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-white/30 hover:text-lime2 transition-colors"><ExternalLink size={16} /></a>
                      <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className="text-white/30 hover:text-lime2 transition-colors"><Pencil size={16} /></button>
                      <button onClick={() => remove(p)} data-testid={`blog-delete-${p.id}`} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#181818] p-6 md:p-8 my-8"
              data-testid="blog-editor"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl font-bold">{editing === "new" ? "New story" : "Edit story"}</h3>
                <button onClick={close} data-testid="blog-editor-close" className="text-white/40 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={save} className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <Input value={form.category} onChange={set("category")} placeholder="Category" data-testid="blog-f-category" className="bg-charcoal border-white/15 h-12" />
                  <Input value={form.author} onChange={set("author")} placeholder="Author" data-testid="blog-f-author" className="bg-charcoal border-white/15 h-12" />
                  <Input type="number" value={form.read_time} onChange={set("read_time")} placeholder="Read time" data-testid="blog-f-readtime" className="bg-charcoal border-white/15 h-12" />
                </div>
                <Input value={form.cover} onChange={set("cover")} placeholder="Cover image URL" data-testid="blog-f-cover" className="bg-charcoal border-white/15 h-12" />

                <div className="rounded-lg border border-white/10 p-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-lime2">English 🇬🇧</p>
                  <Input required value={form.title_en} onChange={set("title_en")} placeholder="Title (EN)" data-testid="blog-f-title" className="bg-charcoal border-white/15 h-12" />
                  <Textarea required value={form.excerpt_en} onChange={set("excerpt_en")} placeholder="Excerpt (EN)" data-testid="blog-f-excerpt" className="bg-charcoal border-white/15 min-h-[70px]" />
                  <Textarea required value={form.content_en} onChange={set("content_en")} placeholder="Content EN (separate paragraphs with a blank line)" data-testid="blog-f-content" className="bg-charcoal border-white/15 min-h-[150px]" />
                </div>

                <div className="rounded-lg border border-white/10 p-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-lime2">Français 🇫🇷</p>
                  <Input value={form.title_fr} onChange={set("title_fr")} placeholder="Titre (FR)" data-testid="blog-f-title-fr" className="bg-charcoal border-white/15 h-12" />
                  <Textarea value={form.excerpt_fr} onChange={set("excerpt_fr")} placeholder="Extrait (FR)" data-testid="blog-f-excerpt-fr" className="bg-charcoal border-white/15 min-h-[70px]" />
                  <Textarea value={form.content_fr} onChange={set("content_fr")} placeholder="Contenu FR (paragraphes séparés par une ligne vide)" data-testid="blog-f-content-fr" className="bg-charcoal border-white/15 min-h-[150px]" />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-f-published" className="accent-[#00A86B] w-4 h-4" />
                    Published
                  </label>
                  <button type="submit" disabled={saving} data-testid="blog-f-save" className="inline-flex items-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full px-6 py-3 transition-colors disabled:opacity-60">
                    {saving ? "Saving..." : "Save story"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
